import React from "react";
import { Button, Form, Modal } from "antd";
import { useTranslation } from "react-i18next";

import { getTraineeToTrainerMap } from "@fitness-app/app-store/src/store/reducers/team/selectors";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { useAppSelector } from "~/store/initializeStore";
import { useTeamMemberTaskContext } from "../context/TeamMemberTaskContext";
import { useCanRouteTaskToClientTrainer } from "../hooks/useCanRouteTaskToClientTrainer";
import { useCreateTeamMemberTask } from "../hooks/useCreateTeamMemberTask";
import { useUpdateTeamMemberTask } from "../hooks/useUpdateTeamMemberTask";
import { type TeamMemberTaskFormModel } from "../types";
import TeamMemberTaskForm from "./TeamMemberTaskForm";

export const TeamMemberTaskFormModal: React.FC = () => {
  const { t } = useTranslation(["dashboard", "common"]);
  const { isFormOpen, closeTaskForm, initialFormData, editingTask } = useTeamMemberTaskContext();
  const { mutateAsync: createTask, isLoading: isCreating } = useCreateTeamMemberTask();
  const { mutation: updateMutation } = useUpdateTeamMemberTask();
  const { memberId } = useUserClaims();
  const canRouteToClientTrainer = useCanRouteTaskToClientTrainer();
  const traineeToTrainerMap = useAppSelector(getTraineeToTrainerMap);
  const [formController] = Form.useForm<TeamMemberTaskFormModel>();

  const isEditing = Boolean(editingTask);
  const isLoading = isCreating || updateMutation.isLoading;

  const handleSubmit = async (formData: TeamMemberTaskFormModel) => {
    try {
      const reminders = formData.reminders?.map((r) => ({
        id: r.id || crypto.randomUUID(),
        date: r.date,
        sent: r.sent || false,
      }));

      if (isEditing && editingTask) {
        await updateMutation.mutateAsync({
          id: editingTask.id,
          data: {
            title: formData.title,
            description: formData.description,
            dueDate: formData.dueDate,
            startDate: formData.startDate,
            endDate: formData.endDate,
            priority: formData.priority,
            category: formData.category,
            traineeId: formData.traineeId,
            reminders,
            tags: formData.tags,
            metadata: formData.metadata,
          },
        });
      } else {
        await createTask({
          title: formData.title,
          description: formData.description,
          dueDate: formData.dueDate,
          startDate: formData.startDate,
          endDate: formData.endDate,
          priority: formData.priority,
          category: formData.category,
          traineeId: formData.traineeId,
          assignedTeamMemberId:
            canRouteToClientTrainer && formData.traineeId
              ? traineeToTrainerMap[formData.traineeId] || null
              : memberId || null,
          reminders,
          tags: formData.tags,
          metadata: formData.metadata,
        });
      }
      closeTaskForm();
      formController.resetFields();
    } catch {
      // Error handled in hooks
    }
  };

  const handleCancel = () => {
    closeTaskForm();
    formController.resetFields();
  };

  return (
    <Modal
      title={isEditing ? t("dashboard:tasks.editTask") : t("dashboard:tasks.newTask")}
      open={isFormOpen}
      onCancel={handleCancel}
      destroyOnClose
      width={600}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={isLoading}>
          {t("common:button.cancel")}
        </Button>,
        <Button key="submit" type="primary" loading={isLoading} onClick={() => formController.submit()}>
          {isEditing ? t("common:button.save") : t("dashboard:tasks.createTask")}
        </Button>,
      ]}
    >
      <div className="py-3">
        <TeamMemberTaskForm formController={formController} onSubmit={handleSubmit} model={initialFormData} />
      </div>
    </Modal>
  );
};
